import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { LogService } from '../core/log.service';
import { AuthService } from './auth.service';
import { AuthState } from './model/auth-state';

interface TokenResponse {
  access_token?: string;
  patient?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthViewModel {
  private _authState$ = new BehaviorSubject(AuthState.INITIALIZING);
  get authState$(): Observable<AuthState> {
    return this._authState$.asObservable();
  }

  private _token$ = new BehaviorSubject('');
  get token$(): Observable<string> {
    return this._token$.asObservable();
  }

  constructor(
    private authService: AuthService,
    private log: LogService,
  ) {
    this.checkExistingToken();
    this.listenForTokenRequest();
  }

  public checkToken(): Observable<void> {
    return this.authService.hasValidToken().pipe(
      take(1),
      map(() => this._authState$.next(AuthState.AUTHORIZED)),
      catchError((err) => this.handleLoginError(err)),
    );
  }

  listenForTokenRequest() {
    /* this event is sent from fhirclient after the token is received
     * the smart.js file was modified to dispatch this event
     * used patch - package
     */
    window.addEventListener('oauthTokenRequest' as any, async (event: CustomEvent) => {
      const authHeader = this.getAuthorizationHeader(event.detail);
      this.setToken(authHeader);
    });
  }

  setCognitoAuthorizationHeader(authHeader: string) {
    this.setToken(authHeader);
  }

  public isLoggedIn$(): Observable<boolean> {
    return this._authState$.pipe(map((state) => state === AuthState.AUTHORIZED));
  }
  private handleLoginError(err: Error | HttpErrorResponse): Observable<void> {
    this.log.error(err);

    if (err instanceof Error) {
      this._authState$.next(AuthState.GATEWAY_ERROR);
    } else {
      switch (err.status) {
        case 401:
          this._authState$.next(AuthState.BAD_SUBSCRIPTION);
          break;
        case 403:
          this._authState$.next(AuthState.BAD_SUBSCRIPTION);
          break;
        default:
          this._authState$.next(AuthState.GATEWAY_ERROR);
      }
    }

    return of();
  }

  public useTestToken(): void {
    this._token$.next('cus_J5hLZEt1Bd09hc');
  }

  private setToken(authHeader: string): void {
    if (authHeader !== '') this._authState$.next(AuthState.INITIALIZING);
    else this._authState$.next(AuthState.NO_ID);

    this._token$.next(authHeader);

    this.checkToken().subscribe();
  }

  // it allows to get the token from the session storage when user is already logged in
  private checkExistingToken(): void {
    const epicData = this.getAuthorizationHeader();

    if (epicData !== '') {
      this.setToken(epicData);
    }
  }

  private getAuthorizationValues(value?: TokenResponse) {
    let epicData: TokenResponse | undefined = value;

    if (!epicData) {
      const keyValue = sessionStorage.getItem('SMART_KEY')?.replace(/['"]+/g, '');

      if (!keyValue) return;

      const epicDataString = sessionStorage.getItem(keyValue);
      const { tokenResponse } = JSON.parse(epicDataString || '{}') as {
        tokenResponse?: { access_token?: string; patient?: string };
      };
      epicData = { ...tokenResponse };
    }

    const organizationKey = sessionStorage.getItem('organizationKey');

    return {
      patient: epicData?.patient,
      token: epicData?.access_token,
      organizationKey,
    };
  }
  // it allows to get the token from the session storage or from the event
  private getAuthorizationHeader(value?: TokenResponse): string {
    const epicData = this.getAuthorizationValues(value);

    if (!epicData || !epicData.token || !epicData.patient || !epicData.organizationKey) {
      return '';
    }

    return `${epicData.organizationKey} ${epicData.patient} ${epicData.token}`;
  }
}
